/**
 * Searchable, paginated table of vendor organizations.
 */

import Avatar from "@cloudscape-design/chat-components/avatar"
import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Input from "@cloudscape-design/components/input"
import Link from "@cloudscape-design/components/link"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import TextFilter from "@cloudscape-design/components/text-filter"
import { useState, useMemo, type CSSProperties } from "react"
import { useNavigate } from "react-router-dom"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import Pagination from "../../components/Pagination/Pagination"
import { useVendorList, useCreateVendor } from "../../hooks/queries/useVendors"
import { usePageParam } from "../../hooks/usePageParam"
import { type CreateVendorRequest, type Vendor } from "../../services/vendorRegistryApi"
import { getAvatarColor, getInitials } from "../../utils/avatar"

const VENDORS_PER_PAGE = 10

const PAGE_STYLE: CSSProperties = { minHeight: "calc(100vh - 60px)" }

const VendorManagement = () => {
  const navigate = useNavigate()
  const isAdmin = useMyAccess().holdsTenantCapability("manage_vendors")
  const [showForm, setShowForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = usePageParam()
  const [nameError, setNameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [formData, setFormData] = useState<CreateVendorRequest>({
    name: "",
    contact_email: "",
    contact_name: "",
    notes: "",
  })

  const { data: vendors = [], isLoading: loading, error: vendorsError } = useVendorList()
  const createVendor = useCreateVendor()

  const loadError = vendorsError ? "Failed to load vendors" : null
  const createError = createVendor.error
    ? (createVendor.error instanceof Error && createVendor.error.message) ||
      "Failed to create vendor"
    : null

  const filteredVendors = useMemo(() => {
    if (!searchTerm) return vendors
    const q = searchTerm.toLowerCase()
    return vendors.filter(
      (v) =>
        v.vendor_name.toLowerCase().includes(q) ||
        (v.vendor_type && v.vendor_type.toLowerCase().includes(q)) ||
        (v.contact_email && v.contact_email.toLowerCase().includes(q)),
    )
  }, [vendors, searchTerm])

  const pageCount = Math.max(1, Math.ceil(filteredVendors.length / VENDORS_PER_PAGE))

  const currentPageVendors = useMemo(() => {
    const start = (currentPage - 1) * VENDORS_PER_PAGE
    return filteredVendors.slice(start, start + VENDORS_PER_PAGE)
  }, [filteredVendors, currentPage])

  const closeForm = () => {
    setShowForm(false)
    setNameError(null)
    setEmailError(null)
    setFormData({ name: "", contact_email: "", contact_name: "", notes: "" })
    createVendor.reset()
  }

  const handleCreate = () => {
    const email = formData.contact_email?.trim()
    const nextNameError = formData.name.trim() ? null : "Name is required"
    const nextEmailError =
      email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? "Enter a valid email address" : null
    setNameError(nextNameError)
    setEmailError(nextEmailError)
    if (nextNameError || nextEmailError) return
    createVendor.mutate(formData, {
      onSuccess: closeForm,
    })
  }

  const columnDefinitions: TableProps.ColumnDefinition<Vendor>[] = [
    {
      id: "vendor",
      header: "Vendor",
      width: "25%",
      cell: (vendor) => (
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <Avatar
            ariaLabel={vendor.vendor_name}
            initials={getInitials(vendor.vendor_name)}
            width={36}
            style={{ root: { background: getAvatarColor(vendor.vendor_id), color: "white" } }}
          />
          <Link
            href={`/vendors/${vendor.vendor_id}`}
            ariaLabel={`View details for ${vendor.vendor_name}`}
            onFollow={(e) => {
              e.preventDefault()
              navigate(`/vendors/${vendor.vendor_id}`)
            }}
          >
            {vendor.vendor_name}
          </Link>
        </SpaceBetween>
      ),
    },
    { id: "type", header: "Type", width: "12%", cell: (v) => v.vendor_type || "-" },
    { id: "email", header: "Contact Email", width: "25%", cell: (v) => v.contact_email || "-" },
    { id: "contactName", header: "Contact Name", width: "20%", cell: (v) => v.contact_name || "-" },
    {
      id: "created",
      header: "Created",
      width: "18%",
      cell: (v) => new Date(v.created_at).toLocaleDateString(),
    },
  ]

  return (
    <div style={PAGE_STYLE}>
      <Breadcrumb items={[{ label: "All Vendors" }]} />

      <ContentLayout
        header={
          <Header
            variant="h1"
            description={`${vendors.length} vendor organizations registered`}
            actions={
              !loading &&
              isAdmin && (
                <Button variant="primary" onClick={() => setShowForm(true)}>
                  + Add Vendor
                </Button>
              )
            }
          >
            Vendor Management
          </Header>
        }
      >
        <SpaceBetween size="l">
          {loadError && <Alert type="error">{loadError}</Alert>}

          <Container disableContentPaddings={false}>
            <Table
              variant="embedded"
              columnDefinitions={columnDefinitions}
              items={currentPageVendors}
              loading={loading}
              loadingText="Loading vendors..."
              trackBy="vendor_id"
              empty={searchTerm ? "No vendors match your search." : "No vendors registered yet."}
              filter={
                <TextFilter
                  filteringText={searchTerm}
                  filteringPlaceholder="Search by name, type, or email..."
                  filteringAriaLabel="Search vendors"
                  onChange={({ detail }) => {
                    setSearchTerm(detail.filteringText)
                    setCurrentPage(1)
                  }}
                />
              }
              pagination={
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  hasMore={false}
                  onNext={() => setCurrentPage((p) => p + 1)}
                  onPrevious={() => setCurrentPage((p) => p - 1)}
                  onPageChange={setCurrentPage}
                />
              }
            />
          </Container>
        </SpaceBetween>
      </ContentLayout>

      {showForm && (
        <Modal
          visible
          onDismiss={closeForm}
          header="Add New Vendor"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={closeForm}>
                  Cancel
                </Button>
                <Button variant="primary" loading={createVendor.isPending} onClick={handleCreate}>
                  Create Vendor
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleCreate()
            }}
          >
            <SpaceBetween size="m">
              {createError && <Alert type="error">{createError}</Alert>}
              <FormField
                label={
                  <>
                    Name <span style={{ color: "#dc2626" }}>*</span>
                  </>
                }
                errorText={nameError}
              >
                <Input
                  value={formData.name}
                  placeholder="e.g., IQVIA"
                  invalid={!!nameError}
                  onChange={({ detail }) => {
                    setFormData((p) => ({ ...p, name: detail.value }))
                    if (nameError) setNameError(null)
                  }}
                />
              </FormField>
              <FormField label="Contact Email" errorText={emailError}>
                <Input
                  type="email"
                  value={formData.contact_email ?? ""}
                  placeholder="data-transfers@vendor.com"
                  invalid={!!emailError}
                  onChange={({ detail }) => {
                    setFormData((p) => ({ ...p, contact_email: detail.value }))
                    if (emailError) setEmailError(null)
                  }}
                />
              </FormField>
              <FormField label="Contact Name">
                <Input
                  value={formData.contact_name ?? ""}
                  placeholder="John Smith"
                  onChange={({ detail }) =>
                    setFormData((p) => ({ ...p, contact_name: detail.value }))
                  }
                />
              </FormField>
            </SpaceBetween>
          </form>
        </Modal>
      )}
    </div>
  )
}

export default VendorManagement
